import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  isValidSlug,
  type BoothCaptureSummary,
  type BoothManifest,
} from '@photobooth/shared';
import { fetchBoothCaptures, fetchBoothManifest } from '../api/client';
import { Button } from '../ui/Button';
import { FooterCredit } from '../ui/FooterCredit';
import { Toast } from '../ui/Toast';
import { PasswordGate } from '../ui/PasswordGate';
import { QrOverlay } from '../ui/QrOverlay';
import { isUnlocked, manifestRequiresPassword } from '../builder/passwordGate';
import { logger } from '../utils/log';
import { trackEvent } from '../utils/analytics';

const log = logger('gallery');

type LoadState = 'loading' | 'ready' | 'not-found' | 'expired' | 'error';

export function GalleryPage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [manifest, setManifest] = useState<BoothManifest | null>(null);
  const [captures, setCaptures] = useState<BoothCaptureSummary[]>([]);
  const [state, setState] = useState<LoadState>('loading');
  const [unlocked, setUnlocked] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [qrVisible, setQrVisible] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!isValidSlug(slug)) {
      setState('not-found');
      return;
    }
    (async () => {
      try {
        const m = await fetchBoothManifest(slug);
        if (cancelled) return;
        if (!m) {
          setState('not-found');
          return;
        }
        if (new Date(m.expiresAt).getTime() < Date.now()) {
          setState('expired');
          return;
        }
        setManifest(m);
        if (!manifestRequiresPassword(m) || isUnlocked(m.slug)) {
          setUnlocked(true);
        }
        const res = await fetchBoothCaptures(slug);
        if (cancelled) return;
        setCaptures(res.captures);
        setState('ready');
      } catch (err) {
        log.warn('gallery load failed', err);
        if (!cancelled) setState('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const galleryUrl = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return `${window.location.origin}/${slug}/gallery`;
  }, [slug]);

  const shareGallery = async () => {
    const title = manifest ? `${manifest.title} — gallery` : 'Photobooth gallery';
    const text = manifest
      ? `Photos from ${manifest.title}`
      : 'Photobooth gallery';
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url: galleryUrl });
        trackEvent('gallery_share', { slug, result: 'shared' });
        return;
      } catch {
        /* user cancelled or share failed — fall through to clipboard */
      }
    }
    try {
      await navigator.clipboard.writeText(galleryUrl);
      trackEvent('gallery_share', { slug, result: 'copied' });
      setToast('Gallery link copied');
    } catch {
      trackEvent('gallery_share', { slug, result: 'fallback' });
      setToast(galleryUrl);
    }
  };

  if (state === 'loading') {
    return (
      <div style={centered}>
        <p style={{ color: 'var(--text-dim)' }}>Loading gallery…</p>
      </div>
    );
  }
  if (state === 'not-found') {
    return (
      <div style={centered}>
        <h1>No such booth</h1>
        <p style={{ color: 'var(--text-dim)' }}>
          The booth at <code>/{slug}</code> doesn’t exist.
        </p>
        <Button variant="primary" onClick={() => navigate('/builder')}>
          Build your own
        </Button>
      </div>
    );
  }
  if (state === 'expired') {
    return (
      <div style={centered}>
        <h1>This booth has expired</h1>
        <Button variant="primary" onClick={() => navigate('/builder')}>
          Build a new one
        </Button>
      </div>
    );
  }
  if (state === 'error') {
    return (
      <div style={centered}>
        <h1>Couldn’t load gallery</h1>
        <p style={{ color: 'var(--text-dim)' }}>Try again in a moment.</p>
      </div>
    );
  }

  if (manifest && !unlocked) {
    return <PasswordGate manifest={manifest} onUnlocked={() => setUnlocked(true)} />;
  }

  return (
    <div
      style={{
        minHeight: '100dvh',
        maxWidth: 1100,
        margin: '0 auto',
        padding: '24px 16px 80px',
        color: 'var(--text)',
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 16,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: 24 }}>
            {manifest?.title ?? slug}
          </h1>
          <p style={{ margin: '4px 0 0', color: 'var(--text-dim)', fontSize: 14 }}>
            {captures.length === 0
              ? 'No photos yet — be the first!'
              : `${captures.length} ${captures.length === 1 ? 'capture' : 'captures'}`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button
            variant="ghost"
            onClick={() => {
              trackEvent('qr_open', { source: 'gallery', slug });
              setQrVisible(true);
            }}
          >
            Show QR
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              trackEvent('wall_open_click', { source: 'gallery', slug });
              navigate(`/${slug}/wall`);
            }}
          >
            Wall view
          </Button>
          <Button variant="secondary" onClick={() => navigate(`/${slug}`)}>
            Open booth
          </Button>
          <Button variant="primary" onClick={() => void shareGallery()}>
            Share gallery
          </Button>
        </div>
      </header>

      {captures.length === 0 ? (
        <div
          style={{
            border: '1px dashed rgba(255,255,255,0.16)',
            borderRadius: 12,
            padding: 40,
            textAlign: 'center',
            color: 'var(--text-dim)',
          }}
        >
          <p style={{ margin: 0 }}>
            Snap your first photo over at{' '}
            <a
              href={`/${slug}`}
              style={{ color: 'var(--accent)' }}
              onClick={(e) => {
                e.preventDefault();
                navigate(`/${slug}`);
              }}
            >
              the booth
            </a>
            .
          </p>
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
            gap: 12,
          }}
        >
          {captures.map((c) => (
            <CaptureTile key={c.id} capture={c} onOpen={() => navigate(c.sharePath)} />
          ))}
        </div>
      )}

      {toast && <Toast message={toast} />}
      {qrVisible && (
        <QrOverlay
          url={`${window.location.origin}/${slug}`}
          title={manifest?.title ?? slug}
          onClose={() => setQrVisible(false)}
        />
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
        <FooterCredit />
      </div>
    </div>
  );
}

function CaptureTile({
  capture,
  onOpen,
}: {
  capture: BoothCaptureSummary;
  onOpen(): void;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      style={{
        position: 'relative',
        background: 'rgba(0,0,0,0.4)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 12,
        overflow: 'hidden',
        aspectRatio: '1 / 1',
        padding: 0,
        cursor: 'pointer',
      }}
    >
      {capture.kind === 'photo' ? (
        <img
          src={capture.mediaUrl}
          alt=""
          loading="lazy"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <video
          src={capture.mediaUrl}
          muted
          playsInline
          preload="metadata"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      )}
      {capture.kind === 'video' && (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            padding: '2px 8px',
            borderRadius: 999,
            background: 'rgba(0,0,0,0.6)',
            color: '#fff',
            fontSize: 11,
            fontWeight: 600,
          }}
        >
          VIDEO
        </span>
      )}
    </button>
  );
}

const centered: React.CSSProperties = {
  minHeight: '100dvh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 12,
  padding: 24,
  color: 'var(--text)',
};
